import { useEffect, useState } from "react";
import { Save, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import ImagePicker from "@/components/ImagePicker";

const TEXT_FIELDS = [
  ["site_name", "Nombre del sitio"],
  ["slogan", "Slogan"],
  ["seo_description", "Descripción SEO"],
  ["email", "Email comercial"],
  ["whatsapp", "WhatsApp"],
  ["address", "Dirección"],
  ["base_city", "Ciudad base"],
  ["instagram", "Instagram URL"],
  ["tiktok", "TikTok URL"],
  ["youtube", "YouTube URL"],
  ["facebook", "Facebook URL"],
  ["x", "X URL"],
  ["linkedin", "LinkedIn URL"],
  ["primary_color", "Color primario (hex)"],
  ["secondary_color", "Color secundario (hex)"],
];

const HERO_FIELDS = [
  ["hero_title", "Título Hero"],
  ["hero_subtitle", "Subtítulo Hero"],
  ["hero_description", "Descripción Hero", true],
  ["hero_cta_primary", "CTA Primario"],
  ["hero_cta_secondary", "CTA Secundario"],
];

export default function AdminSettings() {
  const { setConfig: setGlobal } = useSite();
  const [cfg, setCfg] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { api.get("/site-config").then(setCfg); }, []);

  if (!cfg) return <div>Cargando...</div>;

  const set = (k) => (e) => setCfg({ ...cfg, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async () => {
    const updated = await api.put("/site-config", cfg);
    setGlobal(updated);
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div data-testid="admin-settings">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-1">Configuración</h1>
          <p className="text-sm text-muted-foreground">Datos generales, contacto y hero.</p>
        </div>
        <button onClick={save} data-testid="save-settings" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-sm font-semibold text-sm">
          <Save className="w-4 h-4" /> {saved ? "Guardado" : "Guardar"}
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <section className="border border-border rounded-sm p-6 lg:col-span-2" data-testid="nav-visibility">
          <h3 className="font-display font-bold text-xl mb-2">Visibilidad del menú</h3>
          <p className="text-sm text-muted-foreground mb-5">Mostrar u ocultar secciones del menú principal y footer (Home siempre visible).</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              ["nav_show_about", "Quiénes Somos"],
              ["nav_show_services", "Servicios"],
              ["nav_show_talents", "Talentos"],
              ["nav_show_products", "Productos"],
              ["nav_show_contact", "Contacto"],
            ].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 px-4 py-3 border border-border rounded-sm cursor-pointer hover:border-primary transition-colors" data-testid={`toggle-${k}`}>
                <input
                  type="checkbox"
                  checked={cfg[k] !== false}
                  onChange={set(k)}
                  className="accent-primary w-4 h-4"
                />
                <span className="text-sm font-semibold">{l}</span>
              </label>
            ))}
          </div>
        </section>

        <section className="border border-border rounded-sm p-6">
          <h3 className="font-display font-bold text-xl mb-5">General</h3>
          <div className="space-y-4">
            {TEXT_FIELDS.map(([k, l]) => (
              <div key={k}>
                <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">{l}</label>
                <input type="text" value={cfg[k] || ""} onChange={set(k)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
              </div>
            ))}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={cfg.whatsapp_fab_enabled !== false} onChange={set("whatsapp_fab_enabled")} className="accent-primary" />
              Botón flotante WhatsApp
            </label>
          </div>
        </section>

        <section className="border border-border rounded-sm p-6">
          <h3 className="font-display font-bold text-xl mb-5">Hero</h3>
          <div className="space-y-4">
            {HERO_FIELDS.map(([k, l, multi]) => (
              <div key={k}>
                <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">{l}</label>
                {multi ? (
                  <textarea value={cfg[k] || ""} onChange={set(k)} rows={3} className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none resize-none" />
                ) : (
                  <input type="text" value={cfg[k] || ""} onChange={set(k)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
                )}
              </div>
            ))}
            <div>
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Palabras rotativas (separadas por coma)</label>
              <input type="text" value={(cfg.hero_rotating_words || []).join(", ")} onChange={(e) => setCfg({ ...cfg, hero_rotating_words: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">Tipo de fondo del Hero</label>
              <select value={cfg.hero_background_type || "particles"} onChange={set("hero_background_type")} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none">
                <option value="particles">Partículas / orbs (por defecto)</option>
                <option value="image">Imagen de fondo</option>
                <option value="video">Video de fondo</option>
              </select>
            </div>
            {cfg.hero_background_type === "image" && (
              <ImagePicker label="Imagen de fondo del Hero" value={cfg.hero_background_image || ""} onChange={(v) => setCfg({ ...cfg, hero_background_image: v })} />
            )}
            {cfg.hero_background_type === "video" && (
              <div>
                <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">URL del video (mp4 público)</label>
                <input type="text" value={cfg.hero_background_video || ""} onChange={set("hero_background_video")} placeholder="https://.../video.mp4" className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
              </div>
            )}
          </div>
        </section>
      </div>

      <section className="border border-border rounded-sm p-6 mt-8" data-testid="impact-stats-editor">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-xl">Estadísticas de impacto (Home)</h3>
          <button onClick={() => setCfg({ ...cfg, impact_stats: [...(cfg.impact_stats || []), { label: "", value: 0, suffix: "" }] })} className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-sm text-sm font-semibold hover:border-primary">
            <Plus className="w-4 h-4" /> Añadir
          </button>
        </div>
        <div className="space-y-3">
          {(cfg.impact_stats || []).map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`impact-row-${i}`}>
              <input type="text" placeholder="Etiqueta" value={s.label || ""} onChange={(e) => {
                const arr = [...cfg.impact_stats]; arr[i] = { ...arr[i], label: e.target.value }; setCfg({ ...cfg, impact_stats: arr });
              }} className="col-span-5 h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
              <input type="number" placeholder="Valor" value={s.value ?? 0} onChange={(e) => {
                const arr = [...cfg.impact_stats]; arr[i] = { ...arr[i], value: Number(e.target.value) }; setCfg({ ...cfg, impact_stats: arr });
              }} className="col-span-3 h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
              <input type="text" placeholder="Sufijo" value={s.suffix || ""} onChange={(e) => {
                const arr = [...cfg.impact_stats]; arr[i] = { ...arr[i], suffix: e.target.value }; setCfg({ ...cfg, impact_stats: arr });
              }} className="col-span-2 h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
              <div className="col-span-2 flex gap-1">
                <button type="button" disabled={i === 0} onClick={() => {
                  const arr = [...cfg.impact_stats]; [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]; setCfg({ ...cfg, impact_stats: arr });
                }} title="Subir" data-testid={`impact-up-${i}`} className="flex-1 h-10 border border-border rounded-sm hover:border-primary disabled:opacity-30 inline-flex items-center justify-center">
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button type="button" disabled={i === (cfg.impact_stats || []).length - 1} onClick={() => {
                  const arr = [...cfg.impact_stats]; [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]]; setCfg({ ...cfg, impact_stats: arr });
                }} title="Bajar" data-testid={`impact-down-${i}`} className="flex-1 h-10 border border-border rounded-sm hover:border-primary disabled:opacity-30 inline-flex items-center justify-center">
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => setCfg({ ...cfg, impact_stats: cfg.impact_stats.filter((_, idx) => idx !== i) })} title="Eliminar" className="flex-1 h-10 border border-destructive/40 text-destructive rounded-sm hover:bg-destructive/10 inline-flex items-center justify-center">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {(!cfg.impact_stats || cfg.impact_stats.length === 0) && <p className="text-sm text-muted-foreground">Sin estadísticas. Click "Añadir" para crear una.</p>}
        </div>
      </section>
    </div>
  );
}
